// Render-ready signal for a printable report page.
//
// The PDF renderer (EMAIL-REPORTS-PLAN §4) drives a headless browser at a
// report URL and has to know WHEN to print — and, more importantly, when NOT
// to. `networkidle` is not an answer: a statement whose grower fetch failed
// goes network-idle too, and prints a document with real balances under
// "Statement to: —". A blank or wrong statement reaching a grower is worse
// than no statement at all, so the page states its own verdict and the
// renderer refuses anything that is not `ready`.
//
//   useReportReady(
//     invoicesQ.isPending || growerQ.isPending ? 'pending'
//     : invoicesQ.isError || growerQ.isError || !growerQ.data ? 'error'
//     : rows.length === 0 ? 'empty'
//     : 'ready',
//   )
//
// Exposed on the document element so the renderer can wait on a selector
// without any app code running in its context:
//
//   document.documentElement.dataset.reportState  // pending|ready|error|empty
//
// `empty` is deliberately distinct from `ready`. A genuinely empty statement
// is a legitimate thing to look at on screen, and an illegitimate thing to
// email to a grower as a statement of account — the renderer decides, not
// this hook.
import { useEffect } from 'react'

export type ReportState = 'pending' | 'ready' | 'error' | 'empty'

/** Publishes the page's render verdict for the PDF renderer. No-ops on screen.
 *
 *  `null` means "not mine to say" — used by <RenderRoute/>, which owns the
 *  verdict only while it is establishing the session. Once it hands over to
 *  the report page, the page is the single writer. Two components writing the
 *  same attribute is how a renderer ends up printing someone else's verdict. */
export function useReportReady(state: ReportState | null): void {
  useEffect(() => {
    if (state === null) return
    document.documentElement.dataset.reportState = state
    return () => { delete document.documentElement.dataset.reportState }
  }, [state])
}
